import { useQuery, UseQueryOptions, keepPreviousData, QueryKey } from '@tanstack/react-query';
import { KyronClient } from '../../components/utils/KyronClient';
import { API_V1, DEFAULT_KYRON_CONFIG } from './constants';

export type KyronQueryOptions<TData, TError = Error> = Omit<UseQueryOptions<TData, TError, TData>, 'queryKey'> & {
  queryKey?: QueryKey;
  /**
   * If there are search params in the URL, do not include them in the HTTP request
   */
  excludeSearchParams?: boolean;
};

const client = new KyronClient();
export function queryFn<TData>(apiPath: string, excludeSearchParams?: boolean) {
  return () => client.getDataWithError(API_V1 + apiPath, { excludeSearchParams }) as Promise<TData>;
}

/**
 * @param apiPath - API path to fetch data from
 * @param options - KyronQueryOptions configuration object. See https://tanstack.com/query/latest/docs/react/reference/useQuery
 * @returns see https://tanstack.com/query/latest/docs/react/reference/useQuery
 */
export const useKyronQuery = <TData, TError = Error>(
  apiPath: string,
  options?: KyronQueryOptions<TData, TError>,
): ReturnType<typeof useQuery<TData, TError>> => {
  const { queryKey, excludeSearchParams, ...optionsRest } = options || {};

  // remove search params in apiPath if excludeSearchParams is true
  let modifiedApiPath = apiPath;
  if (excludeSearchParams) {
    // remove search params in apiPath
    const [path] = apiPath.split('?');
    modifiedApiPath = path;
  }

  return useQuery<TData, TError>({
    queryKey: queryKey || [modifiedApiPath],
    queryFn: queryFn<TData>(apiPath, excludeSearchParams),
    ...DEFAULT_KYRON_CONFIG,
    ...optionsRest,
  });
};

// re-export for easy access
export { keepPreviousData };
